import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import Cookies from "js-cookie";
import { toast } from '@/src/lib/toast';
import { API_BASE_URL } from '@/src/utils/config';
import { resolveEuropeanCountryCookieValue } from '@/src/utils/europeanCountryList';
import { clearAllCookiesAndReload } from '@/src/utils/authCookies';
import { getHomePath } from '@/src/routes/routes';

const mutex = new Mutex();

interface IAPIResponse<T = unknown> {
    http_status_code: number;
    status: boolean;
    data: T;
    timestamp: string;
    message: string;
}

export interface IAPIError {
    data?: {
        message?: string;
    };
    error?: string;
}

const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers) => {
        const token = Cookies.get("auth_token") || null
        const deviceId = Cookies.get("device") || ''
        const userId = Cookies.get("userID") || ''
        // headers.set('clientid', API_CLIENT_ID);
        // headers.set('clientsecret', API_CLIENT_SECRET);
        headers.set('device', deviceId);
        headers.set('userID', userId);
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});


//  with all response data
const baseQueryWithAuth: BaseQueryFn<
    string | FetchArgs,
    IAPIResponse,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    await mutex.waitForUnlock();
    try {
        const rawCountry = Cookies.get("user_country_code") || '';
        const country = rawCountry ? resolveEuropeanCountryCookieValue(rawCountry)?.toLowerCase() : '';
        const argsWithCountry: FetchArgs =
            typeof args === 'string'
                ? { url: args, params: { country } }
                : { ...args, params: { country, ...args.params } };
        const result = await baseQuery(argsWithCountry, api, extraOptions);
        const res = result.data as IAPIResponse;
        if (result.error) {
            const errorData = result.error as IAPIError & { status?: number; data?: { data?: { flow?: string }; message?: string } };
            const status = errorData?.status;
            const responseData = errorData?.data;
            const message = (responseData as { message?: string })?.message || "Unknown API error";

            if (status === 401 && typeof window !== 'undefined' && Cookies.get('auth_token')) {
                clearAllCookiesAndReload(getHomePath());
                return { error: { status: "CUSTOM_ERROR", data: { message: "Session expired" }, error: "Session expired" } };
            }

            toast.danger("Error", {
                description: responseData?.message ?? "Unknown error",
                timeout: 2000,
            });
            return {
                error: {
                    status: "CUSTOM_ERROR",
                    data: { message, httpStatus: status },
                    error: message,
                },
            };
        } else {
            return { data: res };
        }

    } catch (error: unknown) {
        let errorResponse: FetchBaseQueryError;
        if (error instanceof Error) {
            errorResponse = {
                status: "CUSTOM_ERROR",
                data: { message: error.message },
                error: error.message,
            };

            toast.danger("Error", {
                description: errorResponse?.error ?? "An unexpected error occurred",
                timeout: 2000,
            });
        } else {
            errorResponse = {
                status: "CUSTOM_ERROR",
                data: { message: "An unexpected error occurred" },
                error: "Unknown error",
            };
        }

        return { error: errorResponse };
    }
};



export const rtkQuerieSetup = createApi({
    reducerPath: 'RTKServices',
    baseQuery: baseQueryWithAuth,
    tagTypes: [
        'UserProfile',
        'Cart',
        'Addresses',
        'Wishlist',
        'MyOrders',
        'GlobalData',
        'RecentlyViewed',
    ],
    endpoints: () => ({}),
});